import { useState } from 'react';
import { useTaskStore } from '../store/taskStore';
import { X, Edit2, Save } from 'lucide-react';
import ImageGallery from './ImageGallery';
import ImageUpload from './ImageUpload';
import FileList from './FileList';
import FileUpload from './FileUpload';
import CommentSection from './CommentSection';
import QuillEditor from './QuillEditor';

export default function TaskDetail() {
  const { selectedTask, setSelectedTask, updateTask } = useTaskStore();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(selectedTask?.title || '');
  const [description, setDescription] = useState(selectedTask?.description || '');

  if (!selectedTask) return null;

  const handleSave = async () => {
    try {
      await updateTask(selectedTask.id, {
        title: title.trim(),
        description: description.trim() || undefined,
      });
      setIsEditing(false);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleClose = () => {
    setSelectedTask(null);
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-soft shadow-soft-xl max-w-7xl w-full h-[90vh] flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-soft-border flex-shrink-0">
          <div className="flex items-center gap-3 flex-1">
            {isEditing ? (
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="flex-1 text-2xl font-bold border-b-2 border-primary-500 focus:outline-none"
              />
            ) : (
              <h2 className="text-2xl font-bold text-gray-900">{selectedTask.title}</h2>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <button
                onClick={handleSave}
                className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <Save className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-soft-muted hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-soft-muted hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Horizontal Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Side - Task Details */}
          <div className="w-1/2 p-6 overflow-y-auto border-r border-soft-border">
            <div className="space-y-6">
              {/* Created By */}
              {selectedTask.created_by && (
                <div className="bg-soft-gray px-4 py-3 rounded-lg">
                  <p className="text-sm text-soft-muted">
                    <span className="font-medium text-gray-900">Oluşturan:</span>{' '}
                    {selectedTask.created_by}
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Açıklama</label>
                {isEditing ? (
                  <QuillEditor
                    value={description}
                    onChange={setDescription}
                    placeholder="Görev açıklaması yazın..."
                  />
                ) : (
                  <div 
                    className="ql-editor text-soft-text"
                    dangerouslySetInnerHTML={{ 
                      __html: selectedTask.description || '<p class="text-gray-400">Açıklama yok</p>' 
                    }}
                  />
                )}
              </div>

              {/* Images */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Görseller</h3>
                <ImageGallery taskId={selectedTask.id} images={selectedTask.images || []} />
                <div className="mt-4">
                  <ImageUpload taskId={selectedTask.id} />
                </div>
              </div>

              {/* Files */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Dosyalar</h3>
                <FileList 
                  taskId={selectedTask.id} 
                  files={(selectedTask.attachments || []).filter(a => !a.is_image)} 
                />
                <div className="mt-4">
                  <FileUpload taskId={selectedTask.id} />
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Comments */}
          <div className="w-1/2 p-6 overflow-y-auto">
            <CommentSection taskId={selectedTask.id} />
          </div>
        </div>
      </div>
    </div>
  );
}
